import { useCallback } from 'react';
import { CalendarException } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { ScheduleTypes } from '@frontend/api-schedule';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { EventData } from '../components/calendar-view/types';
import { getTimeString } from '../utils/date-helpers';

type GetOfficeHoursEventsParamsType = {
  calendarDateValue: string | string[];
  officeHoursData: ScheduleTypes.OfficeHoursWeekSchedule;
  officeExceptions: CalendarException[];
  locationId: string;
};

export const useGetOfficeHoursEvent = () => {
  const { t } = useTranslation('schedule');
  const getOfficeHoursEvents = useCallback(
    ({ calendarDateValue, officeExceptions, officeHoursData, locationId }: GetOfficeHoursEventsParamsType) => {
      if (calendarDateValue && typeof calendarDateValue === 'string' && officeHoursData) {
        const day = format(
          new Date(calendarDateValue),
          'EEEE'
        ).toLocaleLowerCase() as keyof ScheduleTypes.OfficeHoursWeekSchedule;

        const officeHours = officeHoursData?.[day];
        const breaks: EventData[] = [];

        officeExceptions?.map((exception) => {
          const trimName = exception.name?.trim();
          const name = trimName ? trimName : t('Out of Office');
          if (!exception.isAvailable && exception.id && exception.locationId === locationId) {
            breaks.push({
              startHour: getTimeString(exception.startDateTime),
              endHour: getTimeString(exception.endDateTime),
              type: 'unavailable',
              name,
              eventId: exception.id,
              id: exception.id,
              locationId: exception.locationId,
              // NOTE: The event start and end dates could be different than the calendar date in case of multi-day recurring events.
              // But we need to show the event on the calendar for selected calendar date. So, using the calendar date for start and end dates.
              startDate: formatDate(calendarDateValue, 'MM/DD/YYYY'),
              endDate: formatDate(calendarDateValue, 'MM/DD/YYYY'),
            });
          }
        });
        officeHours?.map((officeHour) => {
          officeHour.breaks?.map((breakItem) => {
            if (breakItem.startTime && breakItem.endTime) {
              breaks.push({
                startHour: getTimeString(dayjs(breakItem.startTime, 'HH:mm')),
                endHour: getTimeString(dayjs(breakItem.endTime, 'HH:mm')),
                type: 'unavailable',
                name: t('Break'),
                eventId: `break-${officeHour.dayOfWeek}-${breakItem.startTime}-${breakItem.endTime}`,
                locationId: officeHour.locationId,
                startDate: formatDate(calendarDateValue, 'MM/DD/YYYY'),
                endDate: formatDate(calendarDateValue, 'MM/DD/YYYY'),
              });
            }
          });
        });
        return breaks;
      }
      return [];
    },
    []
  );

  return { getOfficeHoursEvents };
};
